/** B2B terms for all business customers (any plan or feature). Version v2.0 — structured for readable UI. */

/** @typedef {{ type: 'p', text: string }} AgreementBlockP */
/** @typedef {{ type: 'ul', items: string[] }} AgreementBlockUl */
/** @typedef {{ type: 'table', headers: string[], rows: string[][] }} AgreementBlockTable */
/** @typedef {{ type: 'stack', title: string, blocks: (AgreementBlockP | AgreementBlockUl)[] }} AgreementBlockStack */

/**
 * @typedef {{
 *   docTitle: string,
 *   intro: string,
 *   sections: { title: string, blocks: (AgreementBlockP | AgreementBlockUl | AgreementBlockTable | AgreementBlockStack)[] }[]
 * }} BusinessBasicAgreementStructure
 */

/** @type {BusinessBasicAgreementStructure} */
export const SAAS_BUSINESS_BASIC_AGREEMENT_STRUCTURE_HE = {
  docTitle: 'הסכם שימוש בשירותי TripPlanner לעסקים',
  intro:
    'מסמך זה מהווה הסכם בין אבשלום כץ, מפעיל האתר TripPlanner (להלן: "הספק"), לבין העסק המשתמש בשירות (להלן: "הלקוח"), המסדיר את תנאי השימוש, התמחור ומחויבויות הצדדים.',
  sections: [
    {
      title: '1. הגדרת השירות',
      blocks: [
        {
          type: 'p',
          text:
            'TripPlanner היא פלטפורמה המופעלת על ידי הספק ומיועדת למתכנני טיולים מקצועיים. המערכת מאפשרת לבנות טיול, לשתפו עם לקוחות הקצה ולהציג פירוט יומי, מפות, תמונות, קישורים, מסמכים ומידע נוסף. השירות עשוי לכלול, בהתאם למסלול ולבחירת הלקוח, כלי בינה מלאכותית ושליחת הודעות באמצעות WhatsApp.',
        },
      ],
    },
    {
      title: '2. מסלולי השירות ומודל התמחור',
      blocks: [
        {
          type: 'p',
          text:
            '2.1.  הלקוח יבחר מסלול מנוי מתוך המסלולים הזמינים. דמי המנוי החודשיים נפרדים מחיובי השימוש בטיולים.',
        },
        {
          type: 'table',
          headers: ['מאפיין', 'Free', 'Smart', 'Pro', 'Business'],
          rows: [
            ['דמי מנוי חודשיים', '₪0', '₪49', '₪59', '₪159'],
            ['בקשות AI לכל יום טיול', '2', '5', '10', '30'],
            ['הודעות WhatsApp למטייל רשום', '1 לכל טיול', '1 לכל טיול', '10 × ימי הטיול', '20 × ימי הטיול'],
            ['מיתוג אישי בראש המסלול', 'כלול', 'כלול', 'כלול', 'כלול'],
            ['White Label מלא', 'לא כלול', 'לא כלול', 'לא כלול', 'כלול'],
          ],
        },
        {
          type: 'p',
          text:
            '2.2.  בכל המסלולים: עלות שיתוף טיול היא 5 ש"ח לכל יום טיול, ובכל מקרה לא פחות מ 15 ש"ח לטיול; המחיר כולל עד ארבעה משתמשי קצה לטיול (לא כולל המתכנן). עבור כל משתמש קצה נוסף מעבר לארבעה תחול תוספת חד פעמית של 15 ש"ח לאותו טיול. מגבלת אורך הטיול היא 60 ימים, אלא אם הוסכם אחרת בכתב.',
        },
        {
          type: 'p',
          text:
            '2.3.  מכסת הודעות WhatsApp במסלולי Pro ו Business מחושבת לכל מטייל רשום על פני הטיול כולו: מספר ימי הטיול כפול 10 או 20, בהתאמה. אין מדובר בהכרח במגבלת שליחה נפרדת לכל יום. במסלולי Free ו Smart כלולה הודעה אחת למטייל רשום לכל הטיול. הודעות נוספות או חריגות מהמכסה יהיו כפופות לזמינות השירות ולהסכמה נפרדת על תמחור, אם תוצע אפשרות כזו.',
        },
        {
          type: 'p',
          text:
            '2.4.  White Label מלא במסלול Business כולל אפשרות למיתוג בצבעים, בלוגו ובתת דומיין ממותג, וכן תצוגת מסלול ממוקדת ללא תפריטי ניווט לעמודים אחרים באתר TripPlanner. אין בכך התחייבות לחסימה טכנית של כל כתובת אחרת באתר. גישת API או אינטגרציה ייעודית אינה כלולה אוטומטית, ותוסדר בנפרד ככל שתוצע.',
        },
        {
          type: 'p',
          text:
            '2.5.  ללקוחות בעלי היקף פעילות משמעותי ניתן לקבוע תמחור, גישת API ותנאים מותאמים בהסכם נפרד.',
        },
      ],
    },
    {
      title: '3. תנאי שימוש, חיוב ומעבר בין מסלולים',
      blocks: [
        {
          type: 'p',
          text:
            '3.1.  חיובי שימוש בגין טיולים יחולו על טיולים ששיתף הלקוח עם לקוחות קצה באמצעות אפשרויות השיתוף במערכת או שהופק עבורם קובץ PDF דרך המערכת. טיולים שלא שותפו ושלא הופק עבורם PDF לא יחויבו בגין ימי טיול; דמי המנוי החודשיים, ככל שחלים, ייגבו בנפרד.',
        },
        {
          type: 'p',
          text:
            '3.2.  החיוב מתבצע, ככלל, בסוף כל חודש בהתאם לשימוש בפועל ולמסלול המנוי. מסמך חיוב כדין יישלח ללקוח. הספק רשאי לרכז חיובים או לדחות את מועד גבייתם, מבלי לוותר על זכאותו לתשלום.',
        },
        {
          type: 'p',
          text:
            '3.3.  הלקוח רשאי לשנות את מסלול המנוי בהתאם למסלולים הזמינים. שדרוג נכנס לתוקף באופן מיידי; מעבר למסלול נמוך יותר ייכנס לתוקף בתחילת מחזור החיוב הבא. אופן חישוב ההפרש בגין שדרוג באמצע מחזור חיוב יוצג ללקוח טרם אישורו. שינוי מסלול לא יבטל חיובים שכבר נוצרו.',
        },
        {
          type: 'p',
          text:
            '3.4.  מכסות שירות שהוקצו לטיולים ששיתופם כבר החל יישמרו ככלל עד לסיום אותם טיולים; שינוי המסלול יחול על טיולים חדשים החל ממועד כניסתו לתוקף, אלא אם הוצג והוסכם אחרת במפורש.',
        },
        {
          type: 'p',
          text:
            '3.5.  המחירים המפורטים במסמך אינם כוללים מע"מ, ככל שהוא חל לפי דין. חיוב במע"מ ייעשה רק ככל שהספק מחויב לכך לפי דין.',
        },
      ],
    },
    {
      title: '4. שירותי WhatsApp והודעות למטיילים',
      blocks: [
        {
          type: 'p',
          text:
            '4.1.  בהתאם למסלול שנבחר ולהפעלת האפשרויות הרלוונטיות, המערכת מאפשרת שליחת הודעות, תזכורות, מידע על הטיול ועדכונים למטיילים באמצעות WhatsApp. השירות כפוף גם למדיניות, למגבלות ולזמינות של Meta ושל ספקי תקשורת נוספים.',
        },
        {
          type: 'p',
          text:
            '4.2.  הספק פועל לאפשר שליחה תקינה של ההודעות, אולם אינו יכול להבטיח שכל הודעה תימסר, תתקבל במועד או תוצג במכשיר המטייל. עיכוב או אי מסירה שמקורם בנסיבות שאינן בשליטתו הסבירה של הספק, לרבות זמינות שירותי צד שלישי, הגדרות המכשיר או חסימת הודעות, לא יהוו כשלעצמם הפרת התחייבות מצד הספק.',
        },
        {
          type: 'p',
          text:
            '4.3.  הלקוח אחראי לוודא כי מידע חיוני - ובכלל זה שינויים בטיסות, זמני יציאה, נקודות מפגש והנחיות בטיחות - נמסר למטיילים גם באמצעי תקשורת מתאימים נוספים, לפי הצורך. WhatsApp אינו מהווה ערוץ מובטח או בלעדי למסירת מידע דחוף.',
        },
        {
          type: 'p',
          text:
            '4.4.  הלקוח אחראי למסור למטיילים את המידע הנדרש על שימוש במספר הטלפון שלהם ולוודא כי קיימת הרשאה מתאימה לשליחת הודעות בהתאם לדין. אין בסעיף זה כדי לגרוע מאחריות הספק על פי דין בשל מעשה או מחדל שבאחריותו.',
        },
      ],
    },
    {
      title: '5. שימוש בבינה מלאכותית (AI) ועיבוד מסמכים',
      blocks: [
        {
          type: 'p',
          text:
            '5.1.  המערכת עשויה להעמיד לרשות הלקוח כלים לניתוח מסמכים, חילוץ נתונים, ארגון מסלולים, יצירת תוכן והתייעצות באמצעות AI. השימוש בכלים אלה הוא לבחירת הלקוח ובעקבות פעולה שהוא יוזם.',
        },
        {
          type: 'p',
          text:
            '5.2.  בעת שימוש בטעינה או בעיבוד אוטומטי של קובץ, תוכן הקובץ עשוי להישלח לספקי AI חיצוניים לצורך עיבודו. קבצים, כגון כרטיסי טיסה, שוברים ואישורי הזמנה, עשויים לכלול מידע אישי של מטיילים, לרבות שמות, פרטי טיסות ומספרי הזמנה.',
        },
        {
          type: 'p',
          text:
            '5.3.  בעת שימוש בצ׳אט AI או בכלי התייעצות, המערכת עשויה להעביר לספק AI חיצוני את השאלה ואת מבנה הטיול המלא או חלקים ממנו, ובכלל זה ימים, יעדים, תאריכים, לינות, פעילויות ופרטים נוספים שנשמרו בטיול. המידע מועבר לצורך ביצוע הפעולה שהתבקשה.',
        },
        {
          type: 'p',
          text:
            '5.4.  הלקוח אחראי לוודא כי הוא רשאי להזין למערכת את המידע והמסמכים, וכי נמסרו הודעות ונתקבלו הסכמות הנדרשות לפי דין, ככל שנדרשות. מומלץ להימנע מהעלאת מידע אישי שאינו דרוש לצורך הפעולה.',
        },
        {
          type: 'p',
          text:
            '5.5.  תוצרי AI עשויים להכיל טעויות, אי דיוקים או מידע לא מעודכן. הלקוח אחראי לבדוק את התוצרים לפני הסתמכות עליהם או העברתם למטיילים. השימוש בספקי AI כפוף גם למדיניות הפרטיות ולהסדרי עיבוד המידע הרלוונטיים.',
        },
      ],
    },
    {
      title: '6. שימוש בתמונות ממאגרים חיצוניים',
      blocks: [
        {
          type: 'p',
          text:
            'המערכת מאפשרת שילוב תמונות ממאגרי תמונות חיצוניים, כגון Pixabay, לצורך המחשת יעדים, מקומות ופעילויות במסלולי הטיול. התמונות מסופקות על ידי גורמים חיצוניים ונועדו להמחשה בלבד. TripPlanner אינה מתחייבת לדיוקן, לאיכותן או לכך שהן משקפות את המראה או המצב העדכני של המקומות המוצגים בהן. תמונות שמקורן במאגרים אלה עשויות להישמר במאגר התמונות של TripPlanner ולהיות זמינות לשימוש חוזר עבור כלל לקוחות המערכת, בכפוף לתנאי הרישיון החלים עליהן. שימוש חוזר זה מתייחס לתמונות ממאגרים חיצוניים בלבד, ואינו כולל תמונות פרטיות או קבצים שהועלו באופן עצמאי על ידי הלקוח, ואינו כרוך בשיתוף מידע אישי או פרטי הטיולים שלו עם משתמשים אחרים.',
        },
      ],
    },
    {
      title: '7. שמירת מידע והסתייעות בספקים חיצוניים',
      blocks: [
        {
          type: 'p',
          text:
            'מידע שהוזן למערכת על ידי הלקוח נשמר במסגרת החשבון העסקי שלו. לצורך מתן השירותים שהלקוח בחר להפעיל, הספק עשוי להסתייע בספקי אחסון, תשתית, תקשורת, WhatsApp ו AI, ולהעביר אליהם מידע בהיקף הנדרש להפעלת השירות ובהתאם לדין. אין בכך הרשאה לשימוש במידע למטרות שאינן קשורות למתן השירות. תנאי עיבוד המידע, שמירתו והעברתו, לרבות מחוץ לישראל ככל שרלוונטי, יפורטו במדיניות הפרטיות ובהסדרים החלים.',
        },
      ],
    },
    {
      title: '8. הפסקת פעילות השירות',
      blocks: [
        {
          type: 'p',
          text:
            'במקרה של הפסקה יזומה וקבועה של השירות בכללותו, תישלח ללקוח התראה מוקדמת של ארבעה חודשים לפחות, ככל שהדבר בשליטת הספק ומתאפשר בנסיבות העניין. הספק יאפשר ללקוח לייצא את מידע הטיולים השמור עבורו בפורמט PDF לצורך שימוש עתידי, בכפוף למגבלות טכניות ולדין.',
        },
      ],
    },
    {
      title: '9. הגבלת הפצה והעתקה של תכני הטיול',
      blocks: [
        {
          type: 'p',
          text:
            'המערכת כוללת אמצעים להגבלת שיתוף ישיר של מסלולים. עם זאת, לקוחות קצה עשויים להעתיק או להפיץ תוכן באמצעים חיצוניים, כגון צילום מסך או שמירה כ PDF. הספק אינו יכול למנוע כל העתקה או הפצה כאמור, ואינו אחראי למעשי צדדים שלישיים שאינם בשליטתו הסבירה, בכפוף לדין. הלקוח אחראי ליידע את לקוחות הקצה על מגבלות השימוש בתכנים.',
        },
      ],
    },
    {
      title: '10. אישור ההסכם המעודכן',
      blocks: [
        {
          type: 'p',
          text:
            'השימוש בגרסה החדשה של המערכת ובשירותים הנלווים אליה מותנה באישור מפורש של הסכם זה על ידי הלקוח, בין שהוא משתמש חדש ובין שהוא משתמש קיים המבקש להשתמש בגרסה החדשה. האישור יכול להינתן באופן דיגיטלי, והספק רשאי לשמור תיעוד של גרסת ההסכם, מועד האישור וזהות המאשר. היעדר אישור עשוי למנוע גישה לשירותים החדשים.',
        },
      ],
    },
    {
      title: '11. עדכונים ושונות',
      blocks: [
        {
          type: 'p',
          text:
            '11.1.  הספק רשאי להציע עדכונים לתנאי השימוש, למסלולי השירות ולתמחור, בהודעה מוקדמת שתימסר באמצעים מקובלים. שינוי מהותי הטעון הסכמה יובא לאישור הלקוח, בהתאם לדין. שינוי בהסכם שנעשה בהסכמה ספציפית בין הצדדים יתועד בכתב.',
        },
        {
          type: 'p',
          text:
            '11.2.  הסכם זה כפוף לדיני מדינת ישראל. הוראה שאינה ניתנת לאכיפה לא תפגע ביתר הוראות ההסכם.',
        },
      ],
    },
  ],
};

/** @type {BusinessBasicAgreementStructure} */
export const SAAS_BUSINESS_BASIC_AGREEMENT_STRUCTURE_EN = {
  docTitle: 'TripPlanner Business Services Agreement',
  intro:
    'This document constitutes an agreement between Avshalom Katz, operator of the TripPlanner website (the "Provider"), and the business using the service (the "Customer"), and sets out the terms of use, pricing and the obligations of the parties.',
  sections: [
    {
      title: '1. Definition of the Service',
      blocks: [
        {
          type: 'p',
          text:
            "TripPlanner is a platform operated by the Provider and intended for professional trip planners. The system makes it possible to build a trip, share it with end customers, and display a daily breakdown, maps, photos, links, documents and additional information. Depending on the plan and the Customer's choice, the service may include artificial intelligence tools and messaging via WhatsApp.",
        },
      ],
    },
    {
      title: '2. Service Plans and Pricing Model',
      blocks: [
        {
          type: 'p',
          text:
            '2.1.  The Customer shall select a subscription plan from the available plans. Monthly subscription fees are separate from trip usage charges.',
        },
        {
          type: 'table',
          headers: ['Feature', 'Free', 'Smart', 'Pro', 'Business'],
          rows: [
            ['Monthly subscription fee', 'ILS 0', 'ILS 49', 'ILS 59', 'ILS 159'],
            ['AI requests per trip day', '2', '5', '10', '30'],
            ['WhatsApp messages per registered traveler', '1 per trip', '1 per trip', '10 × trip days', '20 × trip days'],
            ['Personal branding at the top of the itinerary', 'Included', 'Included', 'Included', 'Included'],
            ['Full White Label', 'Not included', 'Not included', 'Not included', 'Included'],
          ],
        },
        {
          type: 'p',
          text:
            '2.2.  In all plans: the cost of sharing a trip is ILS 5 per trip day, and in any case no less than ILS 15 per trip. The price includes up to four end users per trip (not including the planner). For each additional end user beyond four, a one-time surcharge of ILS 15 shall apply to that trip. The maximum trip length is 60 days, unless otherwise agreed in writing.',
        },
        {
          type: 'p',
          text:
            '2.3.  The WhatsApp message quota in the Pro and Business plans is calculated per registered traveler over the entire trip: the number of trip days multiplied by 10 or 20, respectively. This is not necessarily a separate daily sending limit. The Free and Smart plans include one message per registered traveler for the entire trip. Additional messages or usage beyond the quota shall be subject to service availability and to a separate agreement on pricing, if such an option is offered.',
        },
        {
          type: 'p',
          text:
            '2.4.  Full White Label in the Business plan includes the option of branding with colors, a logo and a branded subdomain, as well as a focused itinerary view without navigation menus to other pages of the TripPlanner website. This does not constitute a commitment to technically block every other address on the website. API access or a dedicated integration is not automatically included and shall be arranged separately, if offered.',
        },
        {
          type: 'p',
          text:
            '2.5.  For customers with a significant volume of activity, custom pricing, API access and terms may be set out in a separate agreement.',
        },
      ],
    },
    {
      title: '3. Terms of Use, Billing and Changing Plans',
      blocks: [
        {
          type: 'p',
          text:
            "3.1.  Trip usage charges shall apply to trips that the Customer has shared with end customers using the system's sharing options, or for which a PDF file was generated through the system. Trips that were not shared and for which no PDF was generated shall not be charged for trip days; monthly subscription fees, where applicable, shall be collected separately.",
        },
        {
          type: 'p',
          text:
            '3.2.  Billing is, as a rule, carried out at the end of each month according to actual usage and the subscription plan. A lawful billing document shall be sent to the Customer. The Provider may consolidate charges or defer their collection, without waiving its entitlement to payment.',
        },
        {
          type: 'p',
          text:
            '3.3.  The Customer may change its subscription plan in accordance with the available plans. An upgrade takes effect immediately; a move to a lower plan shall take effect at the beginning of the next billing cycle. The method of calculating the difference for an upgrade in the middle of a billing cycle shall be presented to the Customer before approval. A plan change shall not cancel charges already incurred.',
        },
        {
          type: 'p',
          text:
            '3.4.  Service quotas allocated to trips whose sharing has already begun shall, as a rule, be retained until those trips end; the plan change shall apply to new trips from the date it takes effect, unless otherwise expressly presented and agreed.',
        },
        {
          type: 'p',
          text:
            '3.5.  The prices set out in this document do not include VAT, to the extent applicable by law. VAT shall be charged only to the extent the Provider is required to do so by law.',
        },
      ],
    },
    {
      title: '4. WhatsApp Services and Messages to Travelers',
      blocks: [
        {
          type: 'p',
          text:
            '4.1.  Depending on the selected plan and the activation of the relevant options, the system enables sending messages, reminders, trip information and updates to travelers via WhatsApp. The service is also subject to the policies, limitations and availability of Meta and other communication providers.',
        },
        {
          type: 'p',
          text:
            "4.2.  The Provider works to enable proper delivery of messages, but cannot guarantee that every message will be delivered, received on time or displayed on the traveler's device. A delay or non-delivery arising from circumstances beyond the Provider's reasonable control, including the availability of third-party services, device settings or message blocking, shall not in itself constitute a breach of obligation by the Provider.",
        },
        {
          type: 'p',
          text:
            '4.3.  The Customer is responsible for ensuring that essential information - including flight changes, departure times, meeting points and safety instructions - is also delivered to travelers through other appropriate means of communication, as needed. WhatsApp is not a guaranteed or exclusive channel for delivering urgent information.',
        },
        {
          type: 'p',
          text:
            "4.4.  The Customer is responsible for providing travelers with the required information regarding the use of their phone number, and for ensuring that appropriate permission exists for sending messages in accordance with the law. Nothing in this section derogates from the Provider's liability under law for an act or omission for which it is responsible.",
        },
      ],
    },
    {
      title: '5. Use of Artificial Intelligence (AI) and Document Processing',
      blocks: [
        {
          type: 'p',
          text:
            "5.1.  The system may make available to the Customer tools for document analysis, data extraction, itinerary organization, content generation and consultation using AI. The use of these tools is at the Customer's choice and follows an action the Customer initiates.",
        },
        {
          type: 'p',
          text:
            "5.2.  When using automatic file upload or processing, the file's content may be sent to external AI providers for processing. Files such as flight tickets, vouchers and booking confirmations may contain travelers' personal information, including names, flight details and booking numbers.",
        },
        {
          type: 'p',
          text:
            '5.3.  When using an AI chat or consultation tool, the system may transfer to an external AI provider the question and all or part of the trip structure, including days, destinations, dates, accommodations, activities and other details saved in the trip. The information is transferred for the purpose of performing the requested action.',
        },
        {
          type: 'p',
          text:
            '5.4.  The Customer is responsible for ensuring that it is entitled to enter the information and documents into the system, and that the notices required by law have been given and the required consents obtained, to the extent required. It is recommended to avoid uploading personal information that is not needed for the action.',
        },
        {
          type: 'p',
          text:
            '5.5.  AI outputs may contain errors, inaccuracies or outdated information. The Customer is responsible for reviewing the outputs before relying on them or passing them on to travelers. The use of AI providers is also subject to the privacy policy and the relevant data processing arrangements.',
        },
      ],
    },
    {
      title: '6. Use of Images from External Image Libraries',
      blocks: [
        {
          type: 'p',
          text:
            "The platform allows users to incorporate images from external image libraries, such as Pixabay, to illustrate destinations, locations, and activities within travel itineraries. These images are provided by third-party sources and are intended for illustrative purposes only. TripPlanner does not guarantee their accuracy, quality, or that they reflect the current appearance or condition of the locations depicted. Images obtained from these libraries may be stored in TripPlanner's image library and made available for reuse by all customers of the platform, subject to the applicable licensing terms. Such reuse applies exclusively to images obtained from external image libraries and does not include private images or files independently uploaded by customers. It does not involve sharing customers' personal information or itinerary details with other users.",
        },
      ],
    },
    {
      title: '7. Data Retention and Use of External Providers',
      blocks: [
        {
          type: 'p',
          text:
            'Information entered into the system by the Customer is stored within its business account. In order to provide the services the Customer has chosen to activate, the Provider may use hosting, infrastructure, communication, WhatsApp and AI providers, and transfer information to them to the extent required to operate the service and in accordance with the law. This does not grant permission to use the information for purposes unrelated to providing the service. The terms of processing, storing and transferring the information, including outside Israel where relevant, shall be set out in the privacy policy and the applicable arrangements.',
        },
      ],
    },
    {
      title: '8. Discontinuation of the Service',
      blocks: [
        {
          type: 'p',
          text:
            "In the event of a deliberate and permanent discontinuation of the service as a whole, the Customer shall be given advance notice of at least four months, to the extent this is within the Provider's control and possible in the circumstances. The Provider shall allow the Customer to export the trip information stored for it in PDF format for future use, subject to technical limitations and the law.",
        },
      ],
    },
    {
      title: '9. Restriction on Distribution and Copying of Trip Content',
      blocks: [
        {
          type: 'p',
          text:
            'The system includes measures to restrict direct sharing of itineraries. However, end customers may copy or distribute content by external means, such as a screenshot or saving as a PDF. The Provider cannot prevent all such copying or distribution, and is not responsible for the actions of third parties beyond its reasonable control, subject to the law. The Customer is responsible for informing end customers of the restrictions on the use of the content.',
        },
      ],
    },
    {
      title: '10. Acceptance of the Updated Agreement',
      blocks: [
        {
          type: 'p',
          text:
            "Use of the new version of the system and its related services is conditional on the Customer's express acceptance of this agreement, whether it is a new user or an existing user wishing to use the new version. Acceptance may be given digitally, and the Provider may keep a record of the agreement version, the time of acceptance and the identity of the person accepting. Failure to accept may prevent access to the new services.",
        },
      ],
    },
    {
      title: '11. Updates and Miscellaneous',
      blocks: [
        {
          type: 'p',
          text:
            "11.1.  The Provider may propose updates to the terms of use, service plans and pricing, with prior notice given through customary means. A material change requiring consent shall be submitted for the Customer's approval, in accordance with the law. A change to the agreement made by specific agreement between the parties shall be documented in writing.",
        },
        {
          type: 'p',
          text:
            '11.2.  This agreement is governed by the laws of the State of Israel. A provision that is unenforceable shall not affect the remaining provisions of the agreement.',
        },
      ],
    },
  ],
};

/**
 * Returns the agreement in the UI language (not by region/currency).
 * @param {boolean} isHebrew - current UI language is Hebrew
 * @returns {BusinessBasicAgreementStructure}
 */
export function getBusinessBasicAgreementByLanguage(isHebrew) {
  return isHebrew
    ? SAAS_BUSINESS_BASIC_AGREEMENT_STRUCTURE_HE
    : SAAS_BUSINESS_BASIC_AGREEMENT_STRUCTURE_EN;
}

/**
 * Returns the correct agreement based on region.
 * USD (tripplannerit.com) → English.
 * ILS → Hebrew.
 * @param {string} currency - 'USD' or 'ILS'
 * @returns {BusinessBasicAgreementStructure}
 */
export function getBusinessBasicAgreementByRegion(currency) {
  return currency === 'USD'
    ? SAAS_BUSINESS_BASIC_AGREEMENT_STRUCTURE_EN
    : SAAS_BUSINESS_BASIC_AGREEMENT_STRUCTURE_HE;
}
